import useArchiveDocumentInstance from '@/api/mutations/documentInstance/useArchiveDocumentInstance';
import { Button, Modal, ModalProps } from '@payaca/ambrosia';

import * as documentActions from '@payaca/store/documents/documentActions';
import * as uploadsActions from '@payaca/store/uploads/uploadsActions';
import { singularPlural } from '@payaca/utilities/stringUtilities';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import useDeleteUrl from '../../../../../../api/mutations/file/useDeleteUrl';
import projectKeys from '../../../../../../api/queries/project/keyFactory';
import { TFolderEntry } from '../../../../../../api/queries/project/useGetProjectFolderEntries';

type Props = {
  folderEntries: TFolderEntry[];
  projectId: string;
} & Pick<ModalProps, 'isOpen' | 'onClose'>;
const DeleteFilesModal: FC<Props> = (props) => {
  const { isOpen, onClose, folderEntries, projectId } = props;
  const [isDeleting, setIsDeleting] = useState(false);
  const { mutateAsync: deleteUrlMutation } = useDeleteUrl();
  const { mutateAsync: archiveDocumentInstanceMutation } =
    useArchiveDocumentInstance();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleDeleteFolderEntry = async () => {
    if (!folderEntries?.length) return;

    setIsDeleting(true);
    await Promise.allSettled(
      folderEntries.map(
        (folderEntry) =>
          new Promise<void>((resolve) => {
            switch (folderEntry?.__typename) {
              case 'UrlFolderEntry': {
                void deleteUrlMutation({
                  id: folderEntry.url.id,
                }).then(() => resolve());
                break;
              }
              case 'DocumentInstanceFolderEntry':
                void archiveDocumentInstanceMutation({
                  projectId,
                  documentInstanceId: folderEntry.documentInstance.id,
                }).then(() => resolve());
                break;
              case 'DocumentFolderEntry':
                dispatch(
                  documentActions.requestRemoveDocument(
                    +folderEntry.document.privateId,
                    () => resolve()
                  )
                );
                break;
              case 'UploadFolderEntry':
                dispatch(
                  uploadsActions.requestDeleteUpload(
                    +folderEntry.upload.privateId,
                    () => resolve(),
                    true
                  )
                );
                break;
            }
          })
      )
    );
    void queryClient.invalidateQueries({
      queryKey: projectKeys.folderEntries(projectId),
    });
    setIsDeleting(false);
    onClose?.();
  };

  return (
    <Modal
      title={`Delete ${
        folderEntries.length
          ? singularPlural(folderEntries.length, 'File')
          : 'Files'
      }`}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Body>
        <p className="mb-6 text-base">
          Are you sure you want to delete{' '}
          {folderEntries?.length === 1
            ? 'this file'
            : `${folderEntries?.length} files`}
          ?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            className="mr-2"
            variant="secondary"
            disabled={isDeleting}
            onClick={onClose}
          >
            Keep
          </Button>
          <Button
            isProcessing={isDeleting}
            disabled={isDeleting}
            colour="negative"
            onClick={handleDeleteFolderEntry}
          >
            Delete
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};
export default DeleteFilesModal;
